'use client';

import { useEffect, useState } from 'react';
import classNames from 'classnames';

import { LayoutGrid, Check, Loader2, FileText, ToggleLeft, ToggleRight } from 'lucide-react';
import { usePageOperations } from '@/modules/pages/hooks/usePageOperations';
import { Page } from '@/modules/pages/types';
import { useAutoArrangeEnabled } from '@/hooks/useAutoArrangeEnabled';
import { useHideEmptyWidgets } from '@/hooks/useHideEmptyWidgets';

import { Button } from '@/components/atoms';

import { Dropdown, DropdownItem } from '@/components/molecules/Dropdown';
import { Modal, useModalController } from '@/components/molecules/Modal';
import { EditPencilIcon, DeleteIcon, ThreeDotsIcon, DownloadIcon } from '@/components/icons';
import { useNavigate } from '@/hooks/useNavigate';
import { NewPageModal } from '@/components/organisms/NewPageModal/NewPageModal';
import { ContractDetailsDrawer } from '@/components/organisms/ContractDetailsDrawer/ContractDetailsDrawer';
import { usePageActions } from '@/modules';
import { useAuth } from '@/modules/auth';
import { useDashboard, useContractInfo } from '@/modules/dashboard';
import { config } from '@/config/env';
import { canDeletePage, canEditPage, canViewPage } from '@/modules/pages/utils/permissions';
import { isSuperAdmin, canHideNoDataWidgets } from '@/modules/widgets/utils/permissions';
import { isDisabledByProducts } from '@/modules/pages/utils/products';
import { usePage } from '@/modules';
import { gridEndpoints } from '@/modules/grid/api/endpoints';
import { isInternalHost } from '@/utils/internalHost';

interface PageActionsMenuProps {
    page: Page;
    onDelete?: () => void;
    onEdit?: () => void;
    className?: string;
    exportAsPdf?: () => void;
    isExporting?: boolean;
    onRestoreDefault?: (content: number[][]) => void;
}

export const PageActionsMenu = ({
    page,
    onDelete,
    onEdit,
    className = '',
    exportAsPdf,
    isExporting = false,
    onRestoreDefault,
}: PageActionsMenuProps) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [defaultGridStatus, setDefaultGridStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
    const [isContractDrawerOpen, setIsContractDrawerOpen] = useState(false);
    const [showContractDetails, setShowContractDetails] = useState(false);
    const [autoArrangeEnabled, setAutoArrangeEnabled] = useAutoArrangeEnabled();
    const [hideEmptyWidgets, setHideEmptyWidgets] = useHideEmptyWidgets();

    useEffect(() => {
        setShowContractDetails(isInternalHost());
    }, []);

    const deleteModalController = useModalController();
    const editModalController = useModalController();
    const { deletePage } = usePageOperations();
    const { setCurrentPage } = usePageActions()
    const { user } = useAuth();
    const { state: { pages, currentTab } } = usePage();
    const { state: dashboardState } = useDashboard();
    const navigate = useNavigate();

    const selectedDealer = dashboardState?.selectedDealer;
    const contractInfo = useContractInfo({
        dealerRefId: selectedDealer?.dealerRefId,
        enabled: isContractDrawerOpen,
    });

    const canEdit = canEditPage(page, user);
    const canDelete = canDeletePage(page, user);
    const isAdmin = user ? isSuperAdmin(user) : false;
    const canHideNoData = canHideNoDataWidgets(user);

    const handleSetDefaultOrder = async () => {
        if (!page?.id || !user) return;
        setDefaultGridStatus('saving');
        const userGrid = await gridEndpoints.getGrid({
            pageId: page.id,
            pageTabId: currentTab?.id || null,
            userRefId: user.userId,
        });
        if (!userGrid) { setDefaultGridStatus('idle'); return; }
        await gridEndpoints.saveGrid({
            pageId: page.id,
            pageTabId: currentTab?.id || null,
            content: userGrid.content,
        });
        setDefaultGridStatus('saved');
        await new Promise(resolve => setTimeout(resolve, 1500));
        setDefaultGridStatus('idle');
    };
    const handleRestoreDefault = async () => {
        if (!page?.id || !user) return;
        const sharedGrid = await gridEndpoints.getGrid({
            pageId: page.id,
            pageTabId: currentTab?.id || null,
        });
        if (!sharedGrid?.content) return;
        await gridEndpoints.saveGrid({
            pageId: page.id,
            pageTabId: currentTab?.id || null,
            content: sharedGrid.content,
            userRefId: user.userId,
        });
        onRestoreDefault?.(sharedGrid.content);
    };

    const handleDelete = async () => {
        if (!page?.id) return;

        try {
            setIsDeleting(true);
            const success = await deletePage(page.id);

            if (success) {
                deleteModalController.close();

                const visiblePages = pages.filter((p) => canViewPage(p, user) && p.title !== 'Summary');
                const dealerContractedProducts = dashboardState?.dealerContractedProducts?.contractedProducts?.map((p: string) => p.toLowerCase()) || [];

                const isPageDisabled = (page: any) =>
                    isDisabledByProducts(page?.metadata?.products, dealerContractedProducts);

                const firstEnabledPage = visiblePages.find((p) => !isPageDisabled(p));

                if (firstEnabledPage) navigate(`/dashboard?pageId=${firstEnabledPage?.id}`);
            }

        } catch (error) {
            console.error('Error deleting page:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleEdit = () => {
        onEdit?.();
        editModalController.open();
    };

    const dropdownItems: DropdownItem[] = [
        ...(canEdit
            ? [
                {
                    id: 'edit',
                    label: 'Edit Page',
                    icon: <EditPencilIcon />,
                    onClick: handleEdit,
                },
            ]
            : []),
        ...(Boolean(config.SHOW_CONTRACT_DETAILS)
            ? [
                {
                    id: 'contract-details',
                    label: 'Contract Details',
                    icon: <FileText size={16} />,
                    onClick: () => setIsContractDrawerOpen(true),
                },
            ]
            : []),
        {
            id: 'export-pdf',
            label: isExporting ? 'Exporting...' : 'Export as PDF',
            icon: <DownloadIcon />,
            onClick: exportAsPdf,
        },
        ...(isAdmin
            ? [
                {
                    id: 'set-default-order',
                    label: defaultGridStatus === 'saved' ? 'Default grid saved' : 'Set default grid',
                    icon: defaultGridStatus === 'saving'
                        ? <Loader2 size={16} className="animate-spin text-gray-500" />
                        : defaultGridStatus === 'saved'
                            ? <Check size={16} className="text-green-500" />
                            : <LayoutGrid size={16} />,
                    onAsyncClick: handleSetDefaultOrder,
                },
            ]
            : []),
        {
            id: 'restore-default',
            label: 'Reset Widget Order',
            icon: <LayoutGrid size={16} />,
            onAsyncClick: handleRestoreDefault,
        },
        {
            id: 'toggle-auto-arrange',
            label: autoArrangeEnabled ? 'Auto-arrange: On' : 'Auto-arrange: Off',
            icon: autoArrangeEnabled
                ? <ToggleRight size={16} className="text-secondary" />
                : <ToggleLeft size={16} className="text-gray-400" />,
            onClick: () => setAutoArrangeEnabled(!autoArrangeEnabled),
        },
        ...(canHideNoData
            ? [
                {
                    id: 'toggle-hide-empty',
                    label: hideEmptyWidgets ? 'Hide empty widgets: On' : 'Hide empty widgets: Off',
                    icon: hideEmptyWidgets
                        ? <ToggleRight size={16} className="text-secondary" />
                        : <ToggleLeft size={16} className="text-gray-400" />,
                    onClick: () => setHideEmptyWidgets(!hideEmptyWidgets),
                },
            ]
            : []),
        ...(canDelete
            ? [
                {
                    id: 'delete',
                    label: 'Delete Page',
                    icon: <DeleteIcon />,
                    onClick: () => deleteModalController.open(),
                },
            ]
            : []),
    ];


    return (
        <div className={classNames(className, 'ml-1')}>
            <Dropdown
                trigger={
                    <Button
                        variant="ghost"
                        aria-label="Page settings"
                        className="hover:bg-gray-100 rounded-full h-10 w-10 p-1"
                    >
                        <ThreeDotsIcon className="w-5 h-5 text-slate-700" />
                    </Button>
                }
                items={dropdownItems}
                align="right"
                width="w-48"
                showChevron={false}
            />

            <Modal
                title="Delete Page"
                controller={deleteModalController}
                closeOnOverlayClick={!isDeleting}
                closeOnEsc={!isDeleting}
                actionButtons={[
                    <Button
                        key="cancel"
                        variant="light"
                        onClick={deleteModalController.close}
                        disabled={isDeleting}
                    >
                        Cancel
                    </Button>,
                    <Button
                        key="delete"
                        variant="destructive"
                        onClick={handleDelete}
                        isLoading={isDeleting}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                ]}
            >
                <div className="space-y-3">
                    <p className="text-sm text-gray-700 font-medium">
                        Are you sure you want to delete "{page.title}"?
                    </p>
                    <p className="text-sm text-gray-500">
                        This will permanently delete the page and all its widgets. This action cannot be undone.
                    </p>
                </div>
            </Modal>

            <NewPageModal controller={editModalController} page={page} />

            <ContractDetailsDrawer
                isOpen={isContractDrawerOpen}
                onClose={() => setIsContractDrawerOpen(false)}
                dealerName={selectedDealer?.dealerName ?? page.title}
                state={contractInfo.state}
                contractedProducts={contractInfo.contractedProducts}
                error={contractInfo.error}
                onRetry={contractInfo.refetch}
            />
        </div>
    );
};
